import { Policy, Benefit } from './types'

/**
 * V2 version of extractBenefits() util function. Extracts visible benefits
 * (where showBenefit is true) from the current year of a policy and organizes
 * them into a map keyed by benefit type.
 *
 * @example
 *   ;```typescript
 *   const policy = {
 *     benefitInfo: [{
 *       current: true,
 *       benefits: [
 *         { benefitType: 'DEDUCTIBLE', limitValue: 1000, used: 500, showBenefit: true },
 *         { benefitType: 'MAX_ALLOWANCE', limitValue: 5000, used: 2500, showBenefit: false }
 *       ]
 *     }]
 *   }
 *   const benefits = extractBenefits(policy)
 *   // Returns:
 *   // {
 *   //   DEDUCTIBLE: { benefitType: 'DEDUCTIBLE', limitValue: 1000, used: 500, showBenefit: true }
 *   // }
 *   ```
 *
 * @param policy - The insurance policy object to extract benefits from
 * @returns A record of visible benefits mapped by their benefit type, or
 *   undefined if no valid benefits exist
 */
export const extractBenefitsData = (
  policy: Policy = {},
): Record<string, Benefit> | undefined => {
  const benefitInfo = policy.benefitInfo

  if (!benefitInfo || benefitInfo.length === 0) {
    return undefined
  }

  const currentYear = benefitInfo.find(info => info.current === true)
  const benefits = currentYear?.benefits ?? []
  const visibleBenefits = benefits.filter(
    benefit => benefit.showBenefit === true,
  )

  return visibleBenefits.reduce<Record<string, Benefit>>((acc, benefit) => {
    acc[benefit.benefitType] = benefit
    return acc
  }, {})
}
